import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Play, Clock, TrendingUp, Upload, ChevronRight, Zap, Star, Check, Crown, Sparkles, AlertCircle, Trash2 } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { Video, Clip } from '../types';
import { videoApi, formatTimestamp } from '../services/api';
import { getDisplayTitle } from '../utils/videoUtils';
import { optimizedApi } from '../utils/apiOptimizer';
import { retryVideoOperation } from '../utils/retryWithBackoff';
import Modal from '../components/Modal';
import SubscriptionModal from '../components/SubscriptionModal';
import UpgradePromptCard from '../components/UpgradePromptCard';
import TrialBanner from '../components/TrialBanner';
import ConfirmModal from '../components/ConfirmModal';
import OnboardingWalkthrough from '../components/OnboardingWalkthrough';
import FirstClipSuccessModal from '../components/FirstClipSuccessModal';

import { useUpload } from '../contexts/UploadContext';
import { useAuth } from '../contexts/AuthContext';
import { usePolling } from '../contexts/PollingContext';
import { useUserPlan } from '../hooks/useUserPlan';
import { toast } from 'react-toastify';
import UsageUpgradeCard from '../components/UsageUpgradeCard';
import { useAnalytics } from '../hooks/useAnalytics';

interface DashboardStats {
  total_videos: number;
  total_clips: number;
  total_downloads: number;
  processing_time_saved: number;
  videos_processed_today: number;
}

const _API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const Dashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const { 
    addDashboardPolling, 
    removeDashboardPolling, 
    addStatsPolling, 
    removeStatsPolling, 
    addVideoStatusPolling: _addVideoStatusPolling, 
    removeVideoStatusPolling: _removeVideoStatusPolling 
  } = usePolling();
  const { 
    tasks, 
    addProcessingVideo, 
    updateTaskFromVideoStatus: _updateTaskFromVideoStatus 
  } = useUpload();

  // Pre-populate state with cached data if available to eliminate loading flash
  const cachedVideos = optimizedApi.cache.getVideosSync();
  const cachedStats = optimizedApi.cache.getStatsSync();
  const cachedClips = optimizedApi.cache.getClipsSync();

  const persistedVideos = useMemo(() => {
    if (cachedVideos) return null;
    try {
      const stored = sessionStorage.getItem('dashboard_videos');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, [cachedVideos]);

  const persistedStats = useMemo(() => {
    if (cachedStats) return null;
    try {
      const stored = sessionStorage.getItem('dashboard_stats');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, [cachedStats]);

  const persistedClips = useMemo(() => {
    if (cachedClips) return null;
    try {
      const stored = sessionStorage.getItem('dashboard_clips');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, [cachedClips]);
  
  const [videos, setVideos] = useState<Video[]>(() => 
    Array.isArray(cachedVideos) ? cachedVideos :
    Array.isArray(persistedVideos) ? persistedVideos : []
  );
  const [clips, setClips] = useState<Clip[]>(() => 
    Array.isArray(cachedClips) ? cachedClips :
    Array.isArray(persistedClips) ? persistedClips : []
  );
  const [_stats, setStats] = useState<DashboardStats | null>(() => 
    cachedStats && typeof cachedStats === 'object' ? cachedStats as DashboardStats :
    (persistedStats && typeof persistedStats === 'object' ? persistedStats as DashboardStats : null)
  );
  const [loading, setLoading] = useState(!cachedVideos && !persistedVideos); // Only show loading if no cached data
  const [initialLoadComplete, setInitialLoadComplete] = useState(!!cachedVideos || !!persistedVideos); // Mark as complete if we have cached data
  const [_selectedVideo, _setSelectedVideo] = useState<Video | null>(null);
  const [_isUpgradeModalOpen, _setIsUpgradeModalOpen] = useState(false);
  const [videoThumbnails, setVideoThumbnails] = useState<Record<string, string>>({});
  const addedProcessingTasksRef = useRef<Set<string>>(new Set());
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  // Add the missing state variables and imports
  const [activeTab, setActiveTab] = useState<'videos' | 'clips'>('videos');
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showFirstClipSuccess, setShowFirstClipSuccess] = useState(false);
  const [firstClipData, setFirstClipData] = useState<{ videoId: string; videoTitle: string; clipCount: number } | null>(null);
  const { userProfile, refreshUserProfile: _refreshUserProfile, refreshSubscriptionStatus } = useAuth();
  const { isPro, planData: _planData } = useUserPlan();
  const { trackUpgradePromptShown, trackUpgradePromptClicked } = useAnalytics();

  // Cache for video thumbnails to prevent re-fetching
  const thumbnailCache = useMemo(() => new Map<string, string>(), []);

  // Pre-populate thumbnail cache from cached video data
  useEffect(() => {
    if (cachedVideos && Array.isArray(cachedVideos)) {
      const thumbnailData: Record<string, string> = {};
      cachedVideos.forEach((video: any) => {
        // If video has a thumbnail URL, cache it
        if (video.thumbnail_url) {
          thumbnailCache.set(video.id, video.thumbnail_url);
          thumbnailData[video.id] = video.thumbnail_url;
        }
      });
      if (Object.keys(thumbnailData).length > 0) {
        setVideoThumbnails(thumbnailData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Check if user is near or at their limit
  // const isNearLimit = userProfile ? userProfile.videosProcessed >= userProfile.monthlyLimit * 0.8 : false;
  const isAtLimit = userProfile ? userProfile.videosProcessed >= userProfile.monthlyLimit : false;
  const isFreePlan = userProfile?.plan === 'free';
  const monthlyLimit = userProfile?.monthlyLimit || 0;
  const videosUsed = userProfile?.videosProcessed || 0;
  const showUsageCard = isFreePlan && monthlyLimit > 0;
  const isNearLimit = showUsageCard && (monthlyLimit > 0 ? (videosUsed / monthlyLimit) * 100 >= 75 : false);
  const upgradeToastShownRef = useRef(false);
  const usageCardShownRef = useRef(false);

  // Performance optimization: Memoize expensive filtering operations
  const { processingVideos, analyzedVideos: _analyzedVideos, uploadedVideos: _uploadedVideos } = useMemo(() => {
    const processing = videos.filter(video => video.status === 'processing');
    const analyzed = videos.filter(video => video.status === 'analyzed');
    const uploaded = videos.filter(video => video.status === 'uploaded');
    
    return {
      processingVideos: processing,
      analyzedVideos: analyzed,
      uploadedVideos: uploaded
    };
  }, [videos]);

  // Memoize video status check for polling
  const _hasProcessingVideos = useMemo(() => processingVideos.length > 0, [processingVideos.length]);

  // Helper function to calculate time remaining until auto-deletion
  // const getTimeRemaining = (uploadTime: string) => {
  //   const uploaded = new Date(uploadTime);
  //   const now = new Date();
  //   const timeElapsed = now.getTime() - uploaded.getTime();
  //   const timeRemaining = (24 * 60 * 60 * 1000) - timeElapsed; // 24 hours in ms
    
  //   if (timeRemaining <= 0) return 'Expired';
    
  //   const hoursRemaining = Math.floor(timeRemaining / (60 * 60 * 1000));
  //   const minutesRemaining = Math.floor((timeRemaining % (60 * 60 * 1000)) / (60 * 1000));
    
  //   if (hoursRemaining > 0) {
  //     return `${hoursRemaining}h ${minutesRemaining}m`;
  //   } else {
  //     return `${minutesRemaining}m`;
  //   }
  // };

  // Cleanup object URLs when component unmounts
  useEffect(() => {
    return () => {
      // Clean up all object URLs to prevent memory leaks
      Object.values(videoThumbnails).forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      // Note: We don't clear the thumbnail cache here as it should persist across page navigations
    };
  }, [videoThumbnails]);

  const loadThumbnailsForUploadedVideos = useCallback(async (videosList: Video[]) => {
    // Defensive check to ensure videosList is an array
    if (!Array.isArray(videosList)) {
      console.error('loadThumbnailsForUploadedVideos called with non-array:', videosList);
      return;
    }
    
    // Find uploaded videos that don't have clips
    const uploadedVideosWithoutClips = videosList.filter(
      video => video.status === 'uploaded' && (!video.clips || video.clips.length === 0)
    );

    // Check cache first and load cached thumbnails immediately
    const cachedThumbnails: Record<string, string> = {};
    const videosNeedingThumbnails: Video[] = [];

    uploadedVideosWithoutClips.forEach(video => {
      const cachedUrl = thumbnailCache.get(video.id);
      if (cachedUrl) {
        cachedThumbnails[video.id] = cachedUrl;
      } else {
        videosNeedingThumbnails.push(video);
      }
    });

    // Immediately set cached thumbnails
    if (Object.keys(cachedThumbnails).length > 0) {
      setVideoThumbnails(prev => ({ ...prev, ...cachedThumbnails }));
    }

    // Load thumbnails for videos not in cache
        if (videosNeedingThumbnails.length === 0) {
      return; // All thumbnails were cached
    }

    // Set loading state for videos that need thumbnails
    const loadingStates: Record<string, string> = {};
    videosNeedingThumbnails.forEach(video => {
      loadingStates[video.id] = 'loading';
    });
    setVideoThumbnails(prev => ({ ...prev, ...loadingStates }));

    const thumbnailPromises = videosNeedingThumbnails.map(async (video) => {
      try {
        // Use API endpoint for thumbnail generation instead of direct video URL to avoid CORS
        const thumbnailUrl = await videoApi.getVideoThumbnail(video.id, 5);
        thumbnailCache.set(video.id, thumbnailUrl); // Cache the result
        return { videoId: video.id, thumbnailUrl, success: true };
      } catch (error) {
        console.error(`Failed to load thumbnail for video ${video.id}:`, error);
        return { videoId: video.id, thumbnailUrl: null, success: false };
      }
    });

    try {
      const thumbnailResults = await Promise.allSettled(thumbnailPromises);
      const newThumbnails: Record<string, string> = {};
      
      thumbnailResults.forEach((result, index) => {
        const video = videosNeedingThumbnails[index];
        
        if (result.status === 'fulfilled') {
          if (result.value.success && result.value.thumbnailUrl) {
            newThumbnails[result.value.videoId] = result.value.thumbnailUrl;
            // console.log(`✅ [Dashboard] Thumbnail loaded for ${video.filename}`);
          } else {
            // Mark as failed so we don't show loading state
            newThumbnails[result.value.videoId] = 'failed';
            // console.log(`❌ [Dashboard] Thumbnail failed for ${video.filename}`);
          }
        } else {
          // Promise rejected
          newThumbnails[video.id] = 'failed';
          console.error(`❌ [Dashboard] Thumbnail promise rejected for ${video.filename}:`, result.reason);
        }
      });
      
      setVideoThumbnails(prev => {
        const updated = { ...prev };
        Object.entries(newThumbnails).forEach(([videoId, thumbnailUrl]) => {
          if (thumbnailUrl === 'failed') {
            // Remove failed thumbnails from state
            delete updated[videoId];
          } else {
            updated[videoId] = thumbnailUrl;
          }
        });
        return updated;
      });
    } catch (error) {
      console.error('Error loading video thumbnails:', error);
      // Clear loading states for all videos that were being processed
      setVideoThumbnails(prev => {
        const updated = { ...prev };
        videosNeedingThumbnails.forEach(video => {
          if (updated[video.id] === 'loading') {
            delete updated[video.id];
          }
        });
        return updated;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track in-flight fetchData requests to prevent duplicate calls
  const fetchDataInProgressRef = useRef(false);
  
  const fetchData = useCallback(async (forceFresh: boolean = false) => {
    // Don't make API calls if user is not authenticated
    if (!currentUser) {
      // console.log('[Dashboard] Skipping fetchData - user not authenticated');
      return;
    }

    // Prevent duplicate simultaneous calls
    if (fetchDataInProgressRef.current) {
      // console.log('[Dashboard] fetchData already in progress, skipping duplicate call');
      return;
    }

    fetchDataInProgressRef.current = true;

    // Only show loading on initial load, not refreshes
    if (!initialLoadComplete) {
      setLoading(true);
    }
    
    try {
      // If forcing fresh data, invalidate relevant cache entries
      if (forceFresh) {
        optimizedApi.cache.invalidateVideos();
      }
      
      // Use optimized API calls with caching and retry logic
      // Don't let clips API failure prevent videos from loading
      // IMPORTANT: Disable user feedback (toast notifications) for background polling operations
      const [videosRes, statsRes] = await Promise.all([
        retryVideoOperation(() => optimizedApi.getVideos(), 'processing', false).catch(err => {
          console.warn('[Dashboard] Failed to load videos:', err);
          return [];
        }),
        retryVideoOperation(() => optimizedApi.getStats(), 'processing', false).catch(err => {
          console.warn('[Dashboard] Failed to load stats:', err);
          return null;
        })
      ]);
      
      // Load clips separately with error handling so it doesn't block videos
      let clipsRes: any[] = [];
      try {
        clipsRes = await retryVideoOperation(() => videoApi.getExportedClips(), 'processing', false);
        // console.log('Successfully loaded', clipsRes?.length || 0, 'exported clips');
      } catch (error) {
        console.warn('Failed to load exported clips, continuing without them:', error);
        clipsRes = []; // Use empty array as fallback
      }
      
      // Only update state if data has actually changed to prevent unnecessary re-renders
      setVideos(prevVideos => {
        // Ensure videosRes is an array before proceeding
        if (!Array.isArray(videosRes)) {
          console.error('Videos API returned non-array:', videosRes);
          return prevVideos; // Keep previous state if API returned invalid data
        }
        
        const hasChanged = JSON.stringify(prevVideos.map(v => ({ id: v.id, status: v.status, clips: v.clips?.length }))) !== 
                          JSON.stringify(videosRes.map(v => ({ id: v.id, status: v.status, clips: v.clips?.length })));
        return hasChanged ? videosRes : prevVideos;
      });
      
      setClips(prevClips => {
        // Ensure clipsRes is an array before proceeding
        if (!Array.isArray(clipsRes)) {
          console.error('Clips API returned non-array:', clipsRes);
          return prevClips; // Keep previous state if API returned invalid data
        }
        
        const hasChanged = JSON.stringify(prevClips.map(c => c.id)) !== JSON.stringify(clipsRes.map(c => c.id));
        return hasChanged ? clipsRes : prevClips;
      });
      
      setStats(prevStats => {
        const hasChanged = JSON.stringify(prevStats) !== JSON.stringify(statsRes);
        return hasChanged ? statsRes : prevStats;
      });
      
      // Find processing videos and add them to upload widget (avoid duplicates)
      const processingVideos = Array.isArray(videosRes) ? videosRes.filter(video => video.status === 'processing') : [];
      processingVideos.forEach(video => {
        // Skip if we've already added a task for this video in this session
        if (addedProcessingTasksRef.current.has(video.id)) {
          return;
        }
        
        // Only add if there's no existing task for this video
        const existingTask = tasks.find(task => 
          task.videoId === video.id && 
          (task.status === 'analyzing' || task.status === 'uploading' || 
           (task.status === 'completed' && (Date.now() - task.startTime.getTime()) < 30000)) // Within last 30 seconds
        );
        if (!existingTask) {
          addProcessingVideo(video.id, video.filename, video.metadata?.file_size || 0);
          addedProcessingTasksRef.current.add(video.id);
        }
      });
      
      // Load thumbnails for uploaded videos without clips
      if (Array.isArray(videosRes)) {
        loadThumbnailsForUploadedVideos(videosRes);
      }
      
      // Log for debugging - this will help us see if processing videos are detected
      if (processingVideos.length > 0) {
        // console.log(`[Dashboard] Found ${processingVideos.length} processing videos, polling will be started by useEffect`);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Fallback to just videos if clips endpoint fails
      try {
        const [videosRes, statsRes] = await Promise.all([
          videoApi.getVideos(),
          videoApi.getStats()
        ]);
        
        // Only update if different
        setVideos(prevVideos => {
          // Ensure videosRes is an array before proceeding
          if (!Array.isArray(videosRes)) {
            console.error('Fallback Videos API returned non-array:', videosRes);
            return prevVideos; // Keep previous state if API returned invalid data
          }
          
          const hasChanged = JSON.stringify(prevVideos.map(v => ({ id: v.id, status: v.status, clips: v.clips?.length }))) !== 
                            JSON.stringify(videosRes.map(v => ({ id: v.id, status: v.status, clips: v.clips?.length })));
          return hasChanged ? videosRes : prevVideos;
        });
        
        setStats(prevStats => {
          const hasChanged = JSON.stringify(prevStats) !== JSON.stringify(statsRes);
          return hasChanged ? statsRes : prevStats;
        });
        
        // Empty clips on error only if they weren't already empty
        setClips(prevClips => prevClips.length > 0 ? [] : prevClips);
        
        // Still need to check for processing videos in fallback (avoid duplicates)
        const processingVideos = Array.isArray(videosRes) ? videosRes.filter(video => video.status === 'processing') : [];
        processingVideos.forEach(video => {
          // Skip if we've already added a task for this video in this session
          if (addedProcessingTasksRef.current.has(video.id)) {
            return;
          }
          
          // Only add if there's no existing task for this video
          const existingTask = tasks.find(task => 
            task.videoId === video.id && 
            (task.status === 'analyzing' || task.status === 'uploading' || 
             (task.status === 'completed' && (Date.now() - task.startTime.getTime()) < 30000)) // Within last 30 seconds
          );
          if (!existingTask) {
            addProcessingVideo(video.id, video.filename, video.metadata?.file_size || 0);
            addedProcessingTasksRef.current.add(video.id);
          }
        });
        
        if (Array.isArray(videosRes)) {
          loadThumbnailsForUploadedVideos(videosRes);
        }
      } catch (fallbackError) {
        console.error('Fallback data fetch also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
      setInitialLoadComplete(true);
      fetchDataInProgressRef.current = false; // Reset the in-progress flag
    }
  }, [currentUser, loadThumbnailsForUploadedVideos, addProcessingVideo, tasks, addedProcessingTasksRef, initialLoadComplete]);

  useEffect(() => {
    if (videos.length > 0) {
      try {
        sessionStorage.setItem('dashboard_videos', JSON.stringify(videos));
      } catch {
        // ignore
      }
    }
  }, [videos]);

  useEffect(() => {
    if (clips.length > 0) {
      try {
        sessionStorage.setItem('dashboard_clips', JSON.stringify(clips));
      } catch {
        // ignore
      }
    }
  }, [clips]);

  useEffect(() => {
    if (_stats) {
      try {
        sessionStorage.setItem('dashboard_stats', JSON.stringify(_stats));
      } catch {
        // ignore
      }
    }
  }, [_stats]);

  // Show onboarding for new users (0 videos processed)
  useEffect(() => {
    // Check if user is new and hasn't seen onboarding
    const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
    
    if (userProfile && userProfile.videosProcessed === 0 && !hasSeenOnboarding) {
      // Show onboarding after a short delay to let the page load
      const timer = setTimeout(() => {
        setShowOnboarding(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [userProfile]);

  // Detect when user's first video completes processing
  useEffect(() => {
    // Only check if user has exactly 1 video processed (their first one)
    if (userProfile && userProfile.videosProcessed === 1 && videos.length > 0) {
      // Find the first analyzed video with clips
      const firstCompletedVideo = videos.find(v => 
        v.status === 'analyzed' && v.clips && v.clips.length > 0
      );
      
      if (firstCompletedVideo) {
        // Check if we've already shown success for this video
        const hasShownSuccess = sessionStorage.getItem(`firstClipSuccess_${firstCompletedVideo.id}`);
        
        if (!hasShownSuccess) {
          setFirstClipData({
            videoId: firstCompletedVideo.id,
            videoTitle: firstCompletedVideo.filename,
            clipCount: firstCompletedVideo.clips?.length || 0
          });
          setShowFirstClipSuccess(true);
          
          // Mark as shown for this session
          sessionStorage.setItem(`firstClipSuccess_${firstCompletedVideo.id}`, 'true');
        }
      }
    }
  }, [userProfile, videos]);

  // Initial data fetch on component mount
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // console.log('[Dashboard] Component mounted, fetching initial data');
    fetchData();
  }, [fetchData]); // Include fetchData as dependency

  // Fetch data when user becomes authenticated
  useEffect(() => {
    if (currentUser && !authLoading) {
      // console.log('[Dashboard] User authenticated, fetching data');
      // For initial load, don't force fresh data - use cache if available for faster loading
      fetchData(false); // Use cached data for faster initial load
    }
  }, [currentUser, authLoading, fetchData]); // Include fetchData as dependency

  useEffect(() => {
    // Check if we need to refresh due to navigation state (e.g., after video deletion)
    if (location.state?.refresh) {
      // console.log('[Dashboard] Refresh requested via navigation state');
      // Only invalidate specific cache entries instead of all cache
      optimizedApi.cache.invalidateVideos(); // This invalidates videos, analytics, and stats
      // Force a fresh data fetch and clear the state
      setTimeout(() => {
        fetchData(true); // Force fresh data when explicitly refreshing
      }, 100); // Small delay to ensure any pending operations complete
      navigate(location.pathname, { replace: true, state: {} });
    }
    
    // Add visibility change listener but be smarter about when to refresh
    let lastHidden = false;
    const handleVisibilityChange = () => {
      if (!document.hidden && lastHidden) {
        // Only refresh video status (short cache) when returning to tab
        // Don't fetch all data unless we actually need to
        // console.log('[Dashboard] Page became visible after being hidden, checking for updates');
        optimizedApi.cache.invalidate('video_status');
        
        // Only do a full refresh if we have processing videos that might have changed
        const hasProcessingVideos = videos.some(video => 
          video.status === 'processing' || video.status === 'uploaded'
        );
        
        if (hasProcessingVideos) {
          fetchData(false); // Use cached data unless we detect real changes
        }
      }
      lastHidden = document.hidden;
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.refresh, navigate, fetchData, videos]); // Add videos dependency for processing check

  // Handle deleting videos (all types)
  const handleDeleteVideo = useCallback((videoId: string, videoFilename: string, event: React.MouseEvent) => {
    event.preventDefault(); // Prevent navigation to video details
    event.stopPropagation(); // Prevent any parent click handlers
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Video',
      message: `Are you sure you want to delete "${getDisplayTitle(videoFilename)}" and all associated clips? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await videoApi.deleteVideo(videoId);
          
          // Remove from local state immediately for responsive UI
          setVideos(prev => prev.filter(v => v.id !== videoId));
          
          // Clear cached data and refresh
          optimizedApi.cache.invalidateVideos();
          toast.success('Video and clips deleted successfully');
          
          // Refresh data after a short delay
          setTimeout(() => {
            fetchData(true);
          }, 500);
          
        } catch (error: any) {
          console.error('Failed to delete video:', error);
          
          // Handle 404 errors gracefully - video may have already been deleted
          if (error?.response?.status === 404) {
            // Remove from local state and show success since it's effectively deleted
            setVideos(prev => prev.filter(v => v.id !== videoId));
            optimizedApi.cache.invalidateVideos();
            toast.success('Video removed successfully');
            
            // Refresh data after a short delay
            setTimeout(() => {
              fetchData(true);
            }, 500);
          } else {
            toast.error(error.userFriendlyMessage || 'Failed to delete video. Please try again.');
          }
        }
      }
    });
  }, [fetchData]);

  // Monitor videos for processing status changes to start/stop polling
  // This effect will run whenever videos array changes, including after initial fetchData
  useEffect(() => {
    const hasProcessingVideos = videos.some(video => video.status === 'processing' || video.status === 'uploaded');
    
    // Skip aggressive polling if we're on a different page (user navigated away)
    const isOnDashboard = location.pathname === '/';
    
    if (hasProcessingVideos && isOnDashboard) {
      // console.log(`[Dashboard] Starting polling for ${videos.filter(v => v.status === 'processing' || v.status === 'uploaded').length} videos`);
      
      // Start polling if we have processing videos AND we're on the dashboard
      const handleDashboardUpdate = (data: { videos: Video[], stats: DashboardStats }) => {
        // console.log(`[Dashboard] Received polling update: ${data.videos.length} videos, ${data.videos.filter(v => v.status === 'processing').length} processing`);
        
        // Use the polled data directly instead of calling fetchData
        // This prevents unnecessary API calls since polling already gives us fresh video data
        
        // Only update if data has changed to prevent flashing
        setVideos(prevVideos => {
          // Ensure data.videos is an array before proceeding
          if (!Array.isArray(data.videos)) {
            console.error('Dashboard polling received non-array videos:', data.videos);
            return prevVideos; // Keep previous state if data is invalid
          }
          
          const hasChanged = JSON.stringify(prevVideos.map(v => ({ id: v.id, status: v.status, clips: v.clips?.length }))) !== 
                            JSON.stringify(data.videos.map(v => ({ id: v.id, status: v.status, clips: v.clips?.length })));
          if (hasChanged) {
            // Clean up tracking for completed videos
            data.videos.forEach(video => {
              if (video.status === 'analyzed' || video.status === 'error') {
                addedProcessingTasksRef.current.delete(video.id);
              }
            });
            
            // Find processing videos and add them to upload widget (avoid duplicates)
            const processingVideos = data.videos.filter(video => video.status === 'processing');
            processingVideos.forEach(video => {
              // Skip if we've already added a task for this video in this session
              if (addedProcessingTasksRef.current.has(video.id)) {
                return;
              }
              
              // Only add if there's no existing task for this video
              const existingTask = tasks.find(task => 
                task.videoId === video.id && 
                (task.status === 'analyzing' || task.status === 'uploading' || 
                 (task.status === 'completed' && (Date.now() - task.startTime.getTime()) < 30000)) // Within last 30 seconds
              );
              if (!existingTask) {
                addProcessingVideo(video.id, video.filename, video.metadata?.file_size || 0);
                addedProcessingTasksRef.current.add(video.id);
              }
            });
            
            // Load thumbnails for newly uploaded videos
            loadThumbnailsForUploadedVideos(data.videos);
          }
          return hasChanged ? data.videos : prevVideos;
        });
        
        // Only prefetch dashboard data if we have new processed videos (for stats/clips)
        const newlyProcessedVideos = Array.isArray(data.videos) ? data.videos.filter(video => 
          video.status === 'analyzed' && !videos.some(v => v.id === video.id && v.status === 'analyzed')
        ) : [];
        
        if (newlyProcessedVideos.length > 0) {
          // Invalidate related cache when videos are newly processed
          optimizedApi.cache.invalidate('stats');
          optimizedApi.cache.invalidate('clips');
          optimizedApi.cache.invalidateAnalytics();
          
          // Prefetch updated stats and clips in background
          optimizedApi.prefetch.dashboardData();
          
          // Fetch fresh stats and clips data to update UI
          Promise.all([
            optimizedApi.getStats(),
            videoApi.getExportedClips()
          ]).then(([statsRes, clipsRes]) => {
            setStats(statsRes);
            setClips(clipsRes);
          }).catch(error => {
            console.error('Error updating stats/clips after video processing:', error);
          });
        }
      };

      const handleStatsUpdate = (statsData: DashboardStats) => {
        setStats(statsData);
      };

      addDashboardPolling(handleDashboardUpdate);
      addStatsPolling(handleStatsUpdate);
    } else {
      // console.log(`[Dashboard] No processing videos found or not on dashboard, stopping polling`);
      // Stop polling if no processing videos OR not on dashboard
      removeDashboardPolling();
      removeStatsPolling();
    }
    
    // Cleanup polling when effect reruns or component unmounts
    return () => {
      // console.log(`[Dashboard] Cleaning up polling (effect cleanup)`);
      removeDashboardPolling();
      removeStatsPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos, location.pathname, addDashboardPolling, removeDashboardPolling, addStatsPolling, removeStatsPolling, addProcessingVideo, loadThumbnailsForUploadedVideos]); // Include location.pathname as dependency

  // Handle upload completion - this is when we want to force fresh data
  useEffect(() => {
    // Check if any tasks have recently completed and we should refresh
    const recentlyCompletedTasks = tasks.filter(task => 
      task.status === 'completed' && 
      (Date.now() - task.startTime.getTime()) < 10000 // Completed within last 10 seconds
    );

    if (recentlyCompletedTasks.length > 0) {
      // console.log('[Dashboard] Upload tasks completed, fetching fresh data');
      fetchData(true); // Force fresh data when uploads complete
    }
  }, [tasks, fetchData]);

  // Prefetch dashboard data when component mounts to improve perceived performance
  useEffect(() => {
    if (currentUser && !initialLoadComplete) {
      // Prefetch common dashboard data in background while loading
      optimizedApi.prefetch.dashboardData();
    }
  }, [currentUser, initialLoadComplete]);

  const formatDuration = (seconds: number) => {
    const totalSeconds = Math.round(seconds); // Round to nearest second
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    try {
      // Parse the date string properly - handle both ISO and non-ISO formats
      let uploadDate: Date;
      
      // If it's an ISO string without timezone, treat as UTC
      if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
        uploadDate = new Date(dateString + 'Z');  // Force UTC interpretation
      } 
      // If it has timezone info, use as-is
      else if (dateString.includes('Z') || dateString.includes('+') || dateString.includes('-', 10)) {
        uploadDate = new Date(dateString);
      }
      // For other formats, assume local time then convert
      else {
        uploadDate = new Date(dateString);
        // If this appears to be a naive datetime, don't adjust it
      }
      
      const now = new Date();
      
      // Validate the date
      if (isNaN(uploadDate.getTime())) {
        console.error('Invalid date string:', dateString);
        return 'Unknown';
      }
      
      // Calculate difference in milliseconds
      const diffMs = now.getTime() - uploadDate.getTime();
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      // Return relative time for recent dates
      if (diffMinutes < 1) return 'Just now';
      if (diffMinutes < 60) return `${diffMinutes}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      
      // For older dates, show localized date
      return uploadDate.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error parsing date:', dateString, error);
      return 'Unknown';
    }
  };

  // Handle URL query parameters for upgrade flow
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const upgrade = urlParams.get('upgrade');
    
    const cleanUrl = () => {
      urlParams.delete('upgrade');
      navigate({
        pathname: location.pathname,
        search: urlParams.toString() ? `?${urlParams.toString()}` : ''
      }, { replace: true });
    };
    
    if (upgrade === 'cancelled') {
      toast.info('Upgrade cancelled. You can try again anytime!');
      cleanUrl();
    } else if (upgrade === 'success') {
      toast.success('Welcome to Pro! Your account has been upgraded.');
      
      // Sync subscription status and refresh user data
      const syncSubscription = async () => {
        try {
          if (currentUser) {
            const token = await currentUser.getIdToken();
            const response = await fetch('/api/v1/subscription/sync-subscription', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (response.ok) {
              // Use the new AuthContext refresh functions
              await refreshSubscriptionStatus();
              toast.success('Your Pro features are now active!');
            } else {
              console.error('Failed to sync subscription');
              // Still try to refresh data
              await refreshSubscriptionStatus();
            }
          }
        } catch (error) {
          console.error('Error syncing subscription:', error);
          // Still try to refresh data
          try {
            await refreshSubscriptionStatus();
          } catch (refreshError) {
            console.error('Error refreshing data:', refreshError);
          }
        }
      };
      
      syncSubscription();
      cleanUrl();
    } else if (upgrade === 'error') {
      toast.error('There was an issue with your upgrade. Please try again.');
      cleanUrl();
    }
  }, [location.search, navigate, location.pathname, currentUser, refreshSubscriptionStatus]);

  useEffect(() => {
    if (isNearLimit && !upgradeToastShownRef.current) {
      toast.warn('You are almost out of free uploads. Upgrade to keep creating!');
      trackUpgradePromptShown('dashboard_usage_toast', userProfile?.plan || 'free');
      upgradeToastShownRef.current = true;
    } else if (!isNearLimit) {
      upgradeToastShownRef.current = false;
    }
  }, [isNearLimit, trackUpgradePromptShown, userProfile]);

  useEffect(() => {
    if (showUsageCard && !usageCardShownRef.current) {
      trackUpgradePromptShown('dashboard_usage_card', userProfile?.plan || 'free');
      usageCardShownRef.current = true;
    } else if (!showUsageCard) {
      usageCardShownRef.current = false;
    }
  }, [showUsageCard, trackUpgradePromptShown, userProfile]);

  const handleUsageCardUpgradeClick = useCallback(() => {
    trackUpgradePromptClicked('dashboard_usage_card', 'pro');
    setShowSubscriptionModal(true);
  }, [trackUpgradePromptClicked]);

  // Proactively refresh subscription status when Dashboard loads
  useEffect(() => {
    if (currentUser && userProfile) {
      // Quick refresh to ensure we have the latest subscription status
      // Only refresh if we're not handling upgrade parameters to avoid conflicts
      const urlParams = new URLSearchParams(location.search);
      const upgrade = urlParams.get('upgrade');
      
      if (!upgrade) {
        refreshSubscriptionStatus();
      }
    }
  }, [currentUser, userProfile, refreshSubscriptionStatus, location.search]);

  return (
    <>
      <SEOHead 
        title="Dashboard - ClipThis"
        description="Manage your videos and clips with AI-powered content creation tools."
      />
      <div className="relative overflow-x-hidden" style={{ minHeight: 'calc(100vh - 64px)' }}>
        {/* Subtle animated background */}
        <div className="absolute inset-0 dot-pattern-animated opacity-60 pointer-events-none" />
        
        {/* Hero Section */}
        <section className="relative px-6 sm:px-8 pt-6 pb-8 overflow-hidden">
          <div className="absolute inset-0 grid-pattern opacity-[0.08]" />
        
          <div className="max-w-7xl mx-auto">
            <div className="animate-fade-up">
              <h1 className="text-6xl sm:text-7xl font-display font-bold tracking-tighter mb-6 flex items-center">
                Welcome to&nbsp;<span className="text-red-600">CLIPTHIS</span>
                <div className="ml-3 w-3 h-3 bg-black rounded-full animate-pulse" />
              </h1>
              <p className="text-xl text-primary-600 max-w-2xl mb-10">
                AI-powered clip generation for streamers. Transform your content into viral moments with a single click.
              </p>
              <div className="inline-flex items-center space-x-2 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-full text-sm font-semibold mb-8">
                <span>Beta Sale: Lock Pro at early adopter pricing</span>
                <button
                  onClick={() => navigate('/pricing')}
                  className="underline hover:text-red-900"
                >
                  View details
                </button>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/upload" className="btn-danger group">
                  <Upload size={16} className="mr-2" />
                  Clipper
                  <ChevronRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button 
                  onClick={() => setShowFeaturesModal(true)}
                  className="btn-secondary"
                >
                  Learn More
                </button>
              </div>
            </div>
            
          </div>
        </section>

        {showUsageCard && (
          <div className="px-6 sm:px-8 -mt-4 pb-8 animate-fade-up">
            <div className="max-w-5xl mx-auto">
              <UsageUpgradeCard
                videosUsed={videosUsed}
                monthlyLimit={monthlyLimit}
                onUpgrade={handleUsageCardUpgradeClick}
              />
            </div>
          </div>
        )}

      {/* Content Sections */}
      <section className="px-6 sm:px-8 pt-4 pb-16">
        <div className="max-w-7xl mx-auto animate-fade-up">

          {/* Section Header with Tab Navigation */}
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl font-display font-bold tracking-tight mb-2">
                {activeTab === 'videos' ? 'Recent Videos' : 'Recent Clips'}
              </h2>
              <p className="text-primary-600">
                {activeTab === 'videos' 
                  ? 'Your latest uploads and processing status' 
                  : 'Your exported clips'
                }
              </p>
            </div>
            
            {/* Tab Navigation */}
            <div className="flex items-center space-x-1 bg-gray-100 p-1 rounded-xl">
              <button
                onClick={() => setActiveTab('videos')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  activeTab === 'videos' 
                    ? 'bg-white text-black shadow-sm' 
                    : 'text-gray-600 hover:text-black'
                }`}
              >
                Videos
              </button>
              <button
                onClick={() => setActiveTab('clips')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  activeTab === 'clips' 
                    ? 'bg-white text-black shadow-sm' 
                    : 'text-gray-600 hover:text-black'
                }`}
              >
                Clips
              </button>
            </div>
          </div>

          {/* Recent Videos Tab */}
          {activeTab === 'videos' && (
            <div>
              {/* Videos Grid */}
              {loading && !initialLoadComplete ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="aspect-video bg-primary-100 rounded-2xl mb-4" />
                      <div className="h-4 bg-primary-100 rounded-full w-3/4 mb-2" />
                      <div className="h-3 bg-primary-100 rounded-full w-1/2" />
                    </div>
                  ))}
                </div>
              ) : videos.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-20 h-20 bg-primary-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                    <Upload size={32} className="text-primary-400" />
                  </div>
                  <h3 className="text-xl font-display font-semibold mb-2">No videos yet</h3>
                  <p className="text-primary-600 mb-6">Upload your first video to get started</p>
                  <Link to="/upload" className="btn-danger">
                    Go to Clipper
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {videos.map((video: Video, index: number) => (
                    <Link
                      key={video.id}
                      to={`/videos/${video.id}`}
                      className="group block transform transition-all duration-200 hover:scale-105"
                      style={{ animationDelay: `${index * 50}ms` }}
                      onMouseEnter={() => {
                        // Prefetch video details on hover for instant navigation
                        optimizedApi.prefetch.videoDetails(video.id);
                      }}
                    >
                      <div className="bg-white rounded-2xl overflow-hidden border-2 border-gray-200 shadow-md hover:shadow-xl hover:border-gray-300 transition-all duration-300">
                        <div className="relative aspect-video bg-gray-100 relative overflow-hidden rounded-t-2xl">
                          {video.thumbnail_url ? (
                            <img
                              src={video.thumbnail_url}
                              alt={video.filename}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              onError={(e) => {
                                // Hide failed thumbnail and show fallback
                                e.currentTarget.style.display = 'none';
                                const fallback = e.currentTarget.parentElement?.querySelector('.video-fallback') as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                          ) : videoThumbnails[video.id] && videoThumbnails[video.id] !== 'loading' ? (
                            <img
                              src={videoThumbnails[video.id]}
                              alt={video.filename}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              onError={(e) => {
                                // Hide failed thumbnail and show fallback
                                e.currentTarget.style.display = 'none';
                                const fallback = e.currentTarget.parentElement?.querySelector('.video-fallback') as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                          ) : videoThumbnails[video.id] === 'loading' ? (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                            </div>
                          ) : (
                            <div className="video-fallback w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                              <div className="text-center text-gray-400">
                                <Play className="h-8 w-8 mx-auto mb-2" />
                                <p className="text-xs font-medium">Video Preview</p>
                              </div>
                            </div>
                          )}
                          
                          {/* Clips count badge - top right */}
                          {video.clips && video.clips.length > 0 && (
                            <div className="absolute top-2 right-2">
                              <span className="bg-black text-white text-xs px-2 py-1 rounded-md flex items-center">
                                {video.clips.length} clip{video.clips.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                          )}
                          
                          
                          {/* Status overlay - bottom right, only for processing/error */}
                          {(video.status === 'processing' || video.status === 'error') && (
                            <div className="absolute bottom-2 right-2">
                              {video.status === 'processing' && (
                                <span className="bg-gray-700 text-white text-xs px-2 py-1 rounded-md flex items-center">
                                  <Clock size={10} className="mr-1 animate-spin" />
                                  Processing
                                </span>
                              )}
                              {video.status === 'error' && (
                                <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-md flex items-center">
                                  <AlertCircle size={10} className="mr-1" />
                                  Error
                                </span>
                              )}
                            </div>
                          )}
                          
                          {/* Delete button for all videos */}
                          <button
                            className="absolute top-2 left-2 bg-red-600 text-white p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-700 z-10"
                            onClick={(e) => handleDeleteVideo(video.id, video.filename, e)}
                            title="Delete video and all clips"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      
                        <div className="p-4">
                          <h3 className="text-lg font-semibold group-hover:text-primary-600 transition-colors line-clamp-1">
                            {getDisplayTitle(video.filename)}
                          </h3>
                          <div className="flex items-center justify-between text-sm text-gray-600 mt-2">
                            <span>{formatDate(video.upload_time)}</span>
                            <div className="flex items-center gap-2">
                              {(video.status === 'processing' || video.status === 'error') && (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  video.status === 'processing' ? 'bg-red-100 text-red-700' :
                                  video.status === 'error' ? 'bg-red-100 text-red-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {video.status}
                                </span>
                              )}
                              {video.metadata?.duration && (
                                <span className="text-gray-500">
                                  {formatDuration(video.metadata.duration)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Recent Clips Tab */}
          {activeTab === 'clips' && (
            <div>
              {/* Clips Grid */}
              {loading && !initialLoadComplete ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="aspect-video bg-primary-100 rounded-lg mb-3" />
                      <div className="h-3 bg-primary-100 rounded-full w-3/4 mb-2" />
                      <div className="h-3 bg-primary-100 rounded-full w-1/2" />
                    </div>
                  ))}
                </div>
              ) : clips.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-20 h-20 bg-primary-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                    <Sparkles size={32} className="text-primary-400" />
                  </div>
                  <h3 className="text-xl font-display font-semibold mb-2">No exported clips yet</h3>
                  <p className="text-primary-600 mb-6">Upload and analyze a video to create your first clips</p>
                  <Link to="/upload" className="btn-danger">
                    Go to Clipper
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {clips.map((clip: any, index: number) => (
                    <Link
                      key={clip.id}
                      to={`/clips/${clip.id}`}
                      className="group block transform transition-all duration-200 hover:scale-105"
                      style={{ animationDelay: `${index * 50}ms` }}
                      onMouseEnter={() => {
                        // Prefetch clip details for instant navigation
                        optimizedApi.prefetch.clipDetails(clip.id);
                        // Also prefetch video details for the parent video for smooth navigation back
                        if (clip.video_id) {
                          optimizedApi.prefetch.videoDetails(clip.video_id);
                        }
                      }}
                    >
                      <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
                        <div className="aspect-video bg-gray-100 relative">
                          {clip.thumbnail_url ? (
                            <img
                              src={clip.thumbnail_url}
                              alt={clip.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                              <Play className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                          
                          {/* Duration */}
                          <div className="absolute bottom-2 left-2">
                            <span className="bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                              {formatDuration(clip.duration)}
                            </span>
                          </div>
                        </div>
                        
                        <div className="p-3">
                          <h3 className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {clip.title}
                          </h3>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatTimestamp(clip.created_at)}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>


      {/* URL Import Section - Pro Users Only */}
      {isPro && (
        <section className="px-6 sm:px-8 py-16">
          <div className="max-w-7xl mx-auto animate-fade-up">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-display font-bold tracking-tight mb-4">
                Import from URL
              </h2>
              <p className="text-primary-600 text-lg">
                Import videos directly from YouTube and Twitch
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <button
                onClick={() => navigate('/upload')}
                className="w-full bg-white border-2 border-red-500 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] group"
              >
                <div className="flex items-center mb-6">
                  <div className="bg-black rounded-xl mr-4 w-16 h-16">
                    <img src="/Twitch_Youtube.jpg" alt="Import from URL" className="w-full h-full object-cover rounded-xl" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-2xl font-bold text-black group-hover:text-red-600 transition-colors">Upload & Import Videos</h3>
                    <p className="text-gray-600">Upload files or import from YouTube, Twitch, and Kick</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-lg font-semibold text-black mb-3">
                      Ready to Create Amazing Clips?
                    </label>
                    <div className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl bg-gray-50 text-lg text-center text-gray-600">
                      Click here to upload files or import from URLs
                    </div>
                  </div>

                  <div className="w-full bg-red-500 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-red-600 transition-colors shadow-lg group-hover:shadow-xl flex items-center justify-center space-x-3">
                    <Upload className="w-5 h-5" />
                    <span>Go to Clipper</span>
                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-sm font-semibold text-black mb-2">What you can do:</p>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>• Upload video files (MP4, AVI, etc.)</p>
                      <p>• Import from YouTube and Twitch URLs</p>
                      <p>• Configure AI analysis settings</p>
                      <p>• Generate clips automatically</p>
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Pro Features Section - Free Users Only */}
      {!isPro && (
        <section className="px-6 sm:px-8 py-16">
          <div className="max-w-7xl mx-auto animate-fade-up">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-display font-bold tracking-tight mb-4">
                Pro Features Available
              </h2>
              <p className="text-primary-600 text-lg">
                Unlock powerful features with a Pro subscription
              </p>
            </div>
            
            {/* Combined Pro Features Card */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-primary-100 relative overflow-hidden">
              {/* Pro Badge */}
              <div className="absolute top-6 right-6">
                <span 
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-red-500 to-red-600 text-white cursor-pointer hover:from-red-600 hover:to-red-700 transition-all duration-200"
                  onClick={() => setShowSubscriptionModal(true)}
                >
                  <Crown size={12} className="mr-1" />
                  Pro
                </span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* YouTube & Twitch Import */}
                <div>
                  {/* YouTube & Twitch Icons */}
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
                      <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </div>
                    <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                      <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
                        <path d="M11.571 4.714h1.715v5.143H11.571zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24L11.57 19.714h4.286L21.715 13.5V0H6zm14.571 13.143l-2.857 2.857h-2.857l-2.571 2.571v-2.571H9.429V1.714h11.142v11.429z"/>
                      </svg>
                    </div>
                  </div>

                  <h3 className="text-2xl font-display font-bold mb-3">
                    YouTube & Twitch Import
                    <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      ✨ Live Now
                    </span>
                  </h3>
                  <p className="text-primary-600 mb-6">
                    Import videos directly from YouTube and Twitch URLs with just one click
                  </p>

                  <ul className="space-y-3 mb-6">
                    {[
                      'Import from YouTube URLs',
                      'Import from Twitch VODs',
                      'Automatic metadata extraction',
                      'Instant AI processing'
                    ].map((feature, index) => (
                      <li key={index} className="flex items-center space-x-3">
                        <div className="w-5 h-5 bg-gray-900 rounded-full flex items-center justify-center">
                          <Check size={12} className="text-white" />
                        </div>
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* New Pro Tools */}
                <div>
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-6">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 fill-gray-600">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </div>

                  <h3 className="text-2xl font-display font-bold mb-3">
                    Pro Tools
                    <span className="ml-2 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                      <Sparkles size={12} className="mr-1 text-red-600" />
                      New
                    </span>
                  </h3>
                  <p className="text-primary-600 mb-6">
                    Advanced tools for professional content creators
                  </p>

                  <ul className="space-y-3 mb-6">
                    {[
                      'Clip conversion (horizontal to landscape)',
                      'YouTube downloader (MP3 & MP4)',
                      'All export formats',
                      'Custom aspect ratios'
                    ].map((feature, index) => (
                      <li key={index} className="flex items-center space-x-3">
                        <div className="w-5 h-5 bg-gray-900 rounded-full flex items-center justify-center">
                          <Check size={12} className="text-white" />
                        </div>
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Pro Benefits */}
                <div>
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-6">
                    <Zap size={24} className="text-red-600" />
                  </div>

                  <h3 className="text-2xl font-display font-bold mb-3">
                    Pro Benefits
                  </h3>
                  <p className="text-primary-600 mb-6">
                    Unlock the full power of AI-powered content creation
                  </p>

                  <ul className="space-y-3 mb-6">
                    {[
                      'Unlimited video uploads',
                      'Clip conversion (horizontal to landscape)',
                      'YouTube downloader (MP3 & MP4)',
                      'Auto-upload to YouTube Shorts',
                      'Priority processing queue',
                      'Extended storage & history',
                      'Advanced analytics',
                      'Early access to new features'
                    ].map((feature, index) => (
                      <li key={index} className="flex items-center space-x-3">
                        <div className="w-5 h-5 bg-gray-900 rounded-full flex items-center justify-center">
                          <Check size={12} className="text-white" />
                        </div>
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Single CTA Button */}
              <div className="mt-8 pt-6 border-t border-gray-100">
                <button 
                  onClick={() => setShowSubscriptionModal(true)}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center group"
                >
                  <Crown size={16} className="mr-2" />
                  <span>Upgrade to Pro</span>
                  <ChevronRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Upgrade Section - Only show for free users who have processed at least 1 video */}
      {userProfile && userProfile.plan === 'free' && userProfile.videosProcessed > 0 && (
        <section className="px-6 sm:px-8 py-16">
          <div className="max-w-7xl mx-auto animate-fade-up">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Show trial banner if user has used some videos but not hit limit */}
              {!isAtLimit ? (
                <TrialBanner 
                  onStartTrial={() => setShowSubscriptionModal(true)}
                />
              ) : (
                <UpgradePromptCard 
                  onUpgrade={(_plan) => {
                    setShowSubscriptionModal(true);
                    // If a specific plan is selected, we could handle it here
                    // For now, the subscription modal will handle plan selection
                  }}
                  isLimitReached={isAtLimit}
                />
              )}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
                <h3 className="text-xl font-bold mb-4">Why Upgrade?</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                      ⚡
                    </div>
                    <div>
                      <p className="font-medium">10x More Videos Per Month</p>
                      <p className="text-sm text-gray-600">From 3 to 50 videos - create way more content</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                      ✨
                    </div>
                    <div>
                      <p className="font-medium">No Watermarks</p>
                      <p className="text-sm text-gray-600">Professional clips ready for publishing</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                      🚀
                    </div>
                    <div>
                      <p className="font-medium">Priority Processing</p>
                      <p className="text-sm text-gray-600">Get your clips 2x faster</p>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t">
                    <button
                      onClick={() => navigate('/pricing')}
                      className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3 px-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center"
                    >
                      View All Plans & Pricing
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Features Modal */}
      <Modal
        isOpen={showFeaturesModal}
        onClose={() => setShowFeaturesModal(false)}
        title="ClipThis Features"
        size="lg"
      >
        <div className="space-y-6">
          <p className="text-lg text-primary-600">
            Transform your streams into viral content with AI-powered video analysis
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Zap size={16} />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">AI Audio Analysis</h3>
                  <p className="text-sm text-primary-600">Detects excitement, reactions, and highlight moments from audio spikes</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Star size={16} />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Emotion Detection</h3>
                  <p className="text-sm text-primary-600">Analyzes facial expressions to find genuine reactions</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Play size={16} />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Smart Clipping</h3>
                  <p className="text-sm text-primary-600">Automatically creates clips with perfect timing and pacing</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <TrendingUp size={16} />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Viral Optimization</h3>
                  <p className="text-sm text-primary-600">Uses proven patterns to maximize engagement potential</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Clock size={16} />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Time Saving</h3>
                  <p className="text-sm text-primary-600">Process hours of content in minutes, not hours</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Crown size={16} />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Professional Quality</h3>
                  <p className="text-sm text-primary-600">Export-ready clips with optimal format and quality</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-center pt-4">
            <button 
              onClick={() => {
                setShowFeaturesModal(false);
                setShowSubscriptionModal(true);
              }}
              className="btn-primary"
            >
              Get Started Today
            </button>
          </div>
        </div>
      </Modal>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        variant="danger"
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Onboarding Walkthrough for New Users */}
      <OnboardingWalkthrough
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={() => {
          setShowOnboarding(false);
          localStorage.setItem('hasSeenOnboarding', 'true');
        }}
      />

      {/* First Clip Success Modal */}
      {firstClipData && (
        <FirstClipSuccessModal
          isOpen={showFirstClipSuccess}
          onClose={() => setShowFirstClipSuccess(false)}
          videoId={firstClipData.videoId}
          videoTitle={firstClipData.videoTitle}
          clipCount={firstClipData.clipCount}
        />
      )}
    </div>
    </>
  );};

export default Dashboard; 